// Cart "Edit item" modal. Opens from the Edit link on redesigned cart lines,
// loads sections/cart-edit-item.liquid for that product, and swaps the line to
// the chosen variant. The new variant is added before the old line is removed,
// so a failed add never loses the item.

(() => {
  if (window.cartEditItem) return;

  const SECTION = 'cart-edit-item';

  // The mandatory-fee app paints the free-gift wording only on page load, from
  // its "__fees" list (see assets/cart-fee-sync.js). After this flow swaps a
  // line, the app rewrites that list on its own, 1-3 s later, so the swap
  // watches the app's own writes and reloads once the right one lands. Asking
  // for the cart instead would delay that write: the app holds it until no cart
  // request has run for 350 ms.
  //
  // The first write after a swap is often computed from a cart that still held
  // both lines, so it can carry per-mattress fees (state recycling fees) for two
  // mattresses. Done therefore means the list refers to the new variant AND no
  // longer to the old one.
  // ponytail: two edges wait or skip by design, since sizes of one product
  // share the fee rules - an edit from a variant with no fee to one with a fee
  // reloads at once and paints a load later, and an edit from a fee variant to
  // one without waits out the cap. Teach the rule both if a product ever mixes.
  // Measured: the app's correct write lands 2.6-3.5 s after the remove; one
  // slow write was seen past 6 s, so the cap leaves room for that.
  const FEE_SYNC_CAP = 8000;
  // The app ignores cart traffic during its own write and for 800 ms after, so
  // an Update made just after the cart loaded can be dropped. Only then, and
  // only when the app has gone quiet, prompt it with one empty update.
  const NUDGE_AFTER = 2500;
  const NUDGE_QUIET = 1000;
  const NUDGE_LAST = 6500;
  const NUDGE_TIMEOUT = 2500;
  const NUDGE_MAX = 2;

  // Fee helpers live in assets/cart-fee-sync.js, loaded before this file.
  const FeeSync = () => window.CartFeeSync || null;

  const CLOSE_ICON =
    '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  class CartEditItem {
    constructor() {
      this.dialog = null;
      document.addEventListener('click', (event) => {
        const link = event.target.closest('[data-cart-edit]');
        if (!link || !document.getElementById('main-cart-items') || typeof HTMLDialogElement === 'undefined') return;
        event.preventDefault();
        // An Edit link does nothing while a save runs.
        if (!this.busy) this.open(link);
      });
    }

    ensureDialog(closeLabel) {
      if (this.dialog) return this.dialog;
      const dialog = document.createElement('dialog');
      dialog.className = 'cart-edit-dialog';
      dialog.setAttribute('aria-labelledby', 'CartEditTitle');
      dialog.innerHTML = `<div class="cart-edit-dialog__inner"><button type="button" class="cart-edit-dialog__close" data-cart-edit-close>${CLOSE_ICON}<span class="visually-hidden"></span></button><div class="cart-edit-dialog__content" data-cart-edit-content></div><p class="visually-hidden" role="status" data-cart-edit-status></p></div>`;
      dialog.querySelector('[data-cart-edit-close] .visually-hidden').textContent = closeLabel || 'Close';
      dialog.addEventListener('click', (event) => {
        if (this.busy) return;
        if (event.target === dialog || event.target.closest('[data-cart-edit-close]')) dialog.close();
      });
      // Escape would close the dialog mid-save and leave a reload pending.
      // Chrome honours a prevented cancel only once per user activation, so
      // also stop the key itself, at the document: a disabled Update button
      // drops focus to <body>, where a listener on the dialog never hears it.
      dialog.addEventListener('cancel', (event) => {
        if (this.busy) event.preventDefault();
      });
      document.addEventListener(
        'keydown',
        (event) => {
          if (this.busy && dialog.open && event.key === 'Escape') event.preventDefault();
        },
        true
      );
      dialog.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
        // Chrome closes on a second Escape even when cancel is prevented. The
        // page behind is stale by then, so reload as soon as the swap allows.
        if (this.busy) {
          this.closedWhileBusy = true;
          if (this.swapSettled) this.reload();
          return;
        }
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
      document.body.appendChild(dialog);
      this.dialog = dialog;
      return dialog;
    }

    async open(link) {
      this.opener = link;
      this.line = {
        key: link.dataset.lineKey,
        variantId: Number(link.dataset.variantId),
      };
      const dialog = this.ensureDialog(link.dataset.closeLabel);
      const content = dialog.querySelector('[data-cart-edit-content]');
      content.innerHTML = '<div class="cart-edit-dialog__loading" aria-busy="true"></div>';
      document.body.classList.add('overflow-hidden');
      if (!dialog.open) dialog.showModal();

      try {
        const base = link.getAttribute('href').split('?')[0];
        const response = await fetch(`${base}?section_id=${SECTION}&variant=${this.line.variantId}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const body = doc.querySelector('.cart-edit');
        if (!body) throw new Error('Edit content missing');
        content.replaceChildren(body);
        this.bind(body);
      } catch (error) {
        const message = document.createElement('p');
        message.className = 'cart-edit__error cart-edit__error--load';
        message.textContent = link.dataset.errorText || 'There was an error. Please try again.';
        content.replaceChildren(message);
      }
    }

    bind(root) {
      this.root = root;
      this.variants = JSON.parse(root.querySelector('[data-cart-edit-variants]').textContent);
      this.optionEls = Array.from(root.querySelectorAll('[data-option-index]'));
      this.updateButton = root.querySelector('[data-cart-edit-update]');
      this.errorEl = root.querySelector('.cart-edit__error');
      root.addEventListener('change', () => this.refresh());
      this.updateButton.addEventListener('click', () => this.submit());
      // No focus call here: showModal() already focuses the close button, and
      // focusing an option would draw its keyboard focus ring for mouse users.
      this.refresh();
    }

    selectedOptions() {
      return this.optionEls.map((element) => {
        const select = element.querySelector('[data-option-select]');
        if (select) return select.value;
        const checked = element.querySelector('[data-option-radio]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    refresh() {
      if (this.busy) return;
      const options = this.selectedOptions();
      this.variant = this.findVariant(options);

      this.optionEls.forEach((element, index) => {
        element.querySelectorAll('[data-option-radio]').forEach((radio) => {
          const candidate = options.slice();
          candidate[index] = radio.value;
          const match = this.findVariant(candidate);
          const pill = radio.closest('.cart-edit__pill');
          // A variant on sale reads the same way here as it does on the line:
          // struck original, sale price in red.
          const priceEl = pill.querySelector('[data-pill-price]');
          priceEl.textContent = '';
          if (match && match.compare_at_price) {
            const was = document.createElement('s');
            was.className = 'cart-edit__pill-price-was';
            was.textContent = match.compare_at_price;
            const now = document.createElement('span');
            now.className = 'cart-edit__pill-price-sale';
            now.textContent = match.price;
            priceEl.append(was, ' ', now);
          } else if (match) {
            priceEl.textContent = match.price;
          }
          const unavailable = !match || !match.available;
          pill.classList.toggle('is-unavailable', unavailable);
          radio.disabled = unavailable && !radio.checked;
        });

        const select = element.querySelector('[data-option-select]');
        if (select) {
          Array.from(select.options).forEach((option) => {
            const candidate = options.slice();
            candidate[index] = option.value;
            const match = this.findVariant(candidate);
            option.disabled = (!match || !match.available) && !option.selected;
          });
          const preview = element.querySelector('[data-swatch-preview]');
          const swatch = select.selectedOptions[0] ? select.selectedOptions[0].dataset.swatch : '';
          preview.style.background = swatch || '';
          preview.hidden = !swatch;
          element.querySelector('.cart-edit__select').classList.toggle('has-swatch', Boolean(swatch));
        }
      });

      if (this.variant && this.variant.image) {
        const image = this.root.querySelector('.cart-edit__media img');
        if (image) {
          image.removeAttribute('srcset');
          image.src = this.variant.image;
        }
      }

      const unchanged = this.variant && this.variant.id === this.line.variantId;
      this.updateButton.disabled = !this.variant || !this.variant.available || unchanged;
      this.showError('');
    }

    async submit() {
      if (!this.variant || this.busy) return;
      const target = this.variant;
      this.setBusy(true);
      this.closedWhileBusy = false;
      this.swapSettled = false;
      // Once the new variant is in the cart the old line has to go, so a
      // failure after that point reloads instead of offering a retry that
      // would add the item a second time.
      let added = false;
      const sync = { oldId: '', newId: '', oldStillInCart: false, swapped: false, inFlight: 0, lastWriteAt: 0 };
      let markDone;
      const done = new Promise((resolve) => {
        markDone = resolve;
      });
      sync.check = (list) => {
        if (!sync.swapped || !list || !list.refs.has(sync.newId)) return;
        if (sync.oldStillInCart || !list.refs.has(sync.oldId)) markDone();
      };
      // Watch from before the add: the app can queue its first write while our
      // requests are still running.
      const feeSync = FeeSync();
      const stopWatching = feeSync
        ? feeSync.watchFeeWrites(
            () => {
              sync.inFlight += 1;
            },
            (list) => {
              sync.inFlight = Math.max(0, sync.inFlight - 1);
              sync.lastWriteAt = Date.now();
              sync.check(list);
            }
          )
        : () => {};

      try {
        const cart = await (await fetch(`${routes.cart_url}.js`)).json();
        const line =
          cart.items.find((item) => item.key === this.line.key) ||
          cart.items.find((item) => item.variant_id === this.line.variantId);
        if (!line) throw new Error('Line not found');

        const item = { id: target.id, quantity: line.quantity };
        if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
        if (line.selling_plan_allocation) item.selling_plan = line.selling_plan_allocation.selling_plan.id;
        sync.oldId = String(line.variant_id);
        sync.newId = String(target.id);
        const quantityBefore = cart.items
          .filter((cartItem) => cartItem.variant_id === target.id)
          .reduce((total, cartItem) => total + cartItem.quantity, 0);

        await this.addLine(item, quantityBefore);
        added = true;

        const state = await this.removeLine(line.key);
        sync.swapped = true;
        this.swapSettled = true;

        let reason;
        if (!state) reason = 'remove-failed';
        else if (this.closedWhileBusy) reason = 'closed';
        else {
          sync.oldStillInCart = state.items.some((cartItem) => String(cartItem.variant_id) === sync.oldId);
          const baseline = feeSync ? feeSync.fromCart(state) : null;
          // A written list that never referred to the old variant means the
          // product carries no fee, so there is nothing to wait for. An unwritten
          // one means the app has not caught up yet. Without the helpers there is
          // nothing to watch: reload now, and the gift appears once the app's
          // write lands and its own refresh re-renders the line.
          if (!feeSync) reason = 'no-sync';
          else if (baseline && !baseline.refs.has(sync.oldId)) reason = 'no-fee';
          else reason = await this.waitForFees(sync, done);
        }
        try {
          sessionStorage.setItem('cartEditFeeSync', reason);
        } catch (error) {
          // Diagnostics only.
        }
        stopWatching();
        this.reload();
      } catch (error) {
        stopWatching();
        if (added) {
          this.reload();
          return;
        }
        this.setBusy(false);
        // A forced close (Android Back) would leave this error unseen.
        if (this.dialog && !this.dialog.open) {
          document.body.classList.add('overflow-hidden');
          this.dialog.showModal();
        }
        this.showError((this.root && this.root.dataset.errorText) || error.message);
      }
    }

    // Adds the new variant. When the answer is lost (network error), the add
    // may still have reached the cart, so the cart decides: counting it as a
    // failure would let the next Update add the item a second time.
    async addLine(item, quantityBefore) {
      try {
        const response = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [item] }),
        });
        const result = await response.json();
        if (response.ok && !result.status) return;
        const refused = new Error(result.description || result.message || 'Add failed');
        refused.refused = true;
        throw refused;
      } catch (error) {
        if (error.refused) throw error;
        // A request cut off in flight may still be committing, so give it a
        // moment before reading the cart. If the cart cannot be read either,
        // the outcome is unknown: treat it as added and let the reload show
        // the truth, rather than offer a retry that could add it twice.
        await new Promise((resolve) => setTimeout(resolve, 1200));
        let quantityNow;
        try {
          const cart = await (await fetch(`${routes.cart_url}.js`)).json();
          quantityNow = cart.items
            .filter((cartItem) => cartItem.variant_id === item.id)
            .reduce((total, cartItem) => total + cartItem.quantity, 0);
        } catch (readError) {
          return;
        }
        if (quantityNow >= quantityBefore + item.quantity) return;
        throw error;
      }
    }

    // Resolves 'done' when the app has written the new list, 'cap' at the cap,
    // 'closed' if the shopper forced the dialog shut. Failures count as not yet.
    waitForFees(sync, done) {
      return new Promise((resolve) => {
        const started = Date.now();
        let finished = false;
        let nudging = false;
        let nudges = 0;
        const finish = (reason) => {
          if (finished) return;
          finished = true;
          clearTimeout(cap);
          clearInterval(tick);
          resolve(reason);
        };
        const cap = setTimeout(() => finish('cap'), FEE_SYNC_CAP);
        done.then(() => finish('done'));
        const tick = setInterval(async () => {
          if (finished) return;
          if (this.closedWhileBusy) return finish('closed');
          const elapsed = Date.now() - started;
          if (nudging || nudges >= NUDGE_MAX || elapsed < NUDGE_AFTER || elapsed > NUDGE_LAST) return;
          if (sync.inFlight > 0 || Date.now() - sync.lastWriteAt < NUDGE_QUIET) return;
          nudging = true;
          nudges += 1;
          const controller = typeof AbortController === 'function' ? new AbortController() : null;
          const abort = controller ? setTimeout(() => controller.abort(), NUDGE_TIMEOUT) : null;
          try {
            const response = await fetch(`${routes.cart_update_url}.js`, {
              ...fetchConfig(),
              body: '{}',
              signal: controller ? controller.signal : undefined,
            });
            sync.check(FeeSync() ? FeeSync().fromCart(await response.json()) : null);
          } catch (error) {
            // Not yet; the cap still bounds the wait.
          } finally {
            if (abort) clearTimeout(abort);
            nudging = false;
          }
        }, 250);
      });
    }

    // One retry, then give up: the reload shows both lines, which the shopper
    // can fix, rather than a modal that adds the item again on the next click.
    async removeLine(key) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          const state = await response.json();
          if (response.ok && !state.status) return state;
        } catch (error) {
          // Retry once.
        }
      }
      return null;
    }

    setBusy(busy) {
      this.busy = busy;
      // Lock every option while saving; refresh() below re-applies the
      // availability rules when the lock lifts.
      if (this.root) {
        this.root.querySelectorAll('[data-option-radio], [data-option-select]').forEach((control) => {
          control.disabled = busy;
        });
      }
      const status = this.dialog && this.dialog.querySelector('[data-cart-edit-status]');
      if (status) status.textContent = busy ? 'Updating your cart' : '';
      if (!this.updateButton) return;
      if (busy) {
        this.updateButton.disabled = true;
        this.updateButton.setAttribute('aria-busy', 'true');
      } else {
        this.updateButton.removeAttribute('aria-busy');
        this.refresh();
      }
    }

    // Reload rather than re-render: the fee app only paints on page load. The
    // modal stays busy until the new page replaces this one.
    reload() {
      if (this.reloading) return;
      this.reloading = true;
      window.location.reload();
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }

  window.cartEditItem = new CartEditItem();
})();
